import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface WorkSafetyEvent {
  id: number;
  projectId: number;
  projectName?: string;
  date: string;
  type: string;
  severity: string;
  description: string;
  isInternal?: boolean;
}

export interface ProjectSafetyEventFrequency {
  projectId: number;
  projectName: string;
  totalEvents: number;
  totalHoursWorked: number;
  ltqTotalHoursWorked: number;
  eventFrequencyRate: number;
}

/**
 * Per-year safety aggregates from `GET /safety/yearlyStatistics`.
 * `eventFrequencyRate` is the BASE metric — Finnish incident frequency (tapaturmataajuus),
 * per 1,000,000 hours. `oshaIncidenceRate` is the SECONDARY sibling (per 200,000 hours);
 * it equals `eventFrequencyRate / 5` and is optional — the client derives it when absent.
 */
export interface YearlySafetyStatistics {
  year: number;
  totalEvents: number;
  totalHoursWorked: number;
  ltqTotalHoursWorked: number;
  eventFrequencyRate: number;
  oshaIncidenceRate?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SafetyService {
  private apiUrl = `${environment.apiRoot}safety`;

  constructor(private http: HttpClient) {}

  // Get all safety events
  getAllEvents(): Observable<WorkSafetyEvent[]> {
    return this.http.get<WorkSafetyEvent[]>(`${this.apiUrl}/all`);
  }

  // Get a single safety event by ID
  getEvent(id: number): Observable<WorkSafetyEvent> {
    return this.http.get<WorkSafetyEvent>(`${this.apiUrl}/${id}`);
  }

  // Get all events for a specific project
  getEventsByProject(projectId: number): Observable<WorkSafetyEvent[]> {
    return this.http.get<WorkSafetyEvent[]>(`${this.apiUrl}/byProject/${projectId}`);
  }

  // Create a new safety event
  createEvent(event: WorkSafetyEvent): Observable<WorkSafetyEvent> {
    return this.http.post<WorkSafetyEvent>(`${this.apiUrl}`, event);
  }

  // Update an existing safety event
  updateEvent(event: WorkSafetyEvent): Observable<WorkSafetyEvent> {
    return this.http.put<WorkSafetyEvent>(`${this.apiUrl}`, event);
  }

  // Delete a safety event
  deleteEvent(id: number): Observable<WorkSafetyEvent> {
    return this.http.delete<WorkSafetyEvent>(`${this.apiUrl}/${id}`);
  }

  // Get accident frequency for all projects
  getAllEventFrequencies(): Observable<ProjectSafetyEventFrequency[]> {
    return this.http.get<ProjectSafetyEventFrequency[]>(`${this.apiUrl}/frequency`);
  }
  getFrequencyByYear(year: number, includeExternal: boolean = false): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/globalFrequency/${year}`, {
      params: { includeExternal: includeExternal.toString() }
    });
  }

  // Get per-year safety aggregates (hours, events, frequency, OSHA) for every recorded year
  getYearlyStatistics(includeExternal: boolean = false): Observable<YearlySafetyStatistics[]> {
    return this.http.get<YearlySafetyStatistics[]>(`${this.apiUrl}/yearlyStatistics`, {
      params: { includeExternal: includeExternal.toString() }
    });
  }
  // Get accident frequency for a specific project
  getEventFrequencyByProject(projectId: number): Observable<ProjectSafetyEventFrequency> {
    return this.http.get<ProjectSafetyEventFrequency>(`${this.apiUrl}/frequency/${projectId}`);
  }

  // Get the last event for a project
  getLastEvent(projectId: number): Observable<WorkSafetyEvent> {
    return this.http.get<WorkSafetyEvent>(`${this.apiUrl}/lastestEvent/${projectId}`);
  }

  // Get the number of days since the last event for a project
  getDaysWithoutEvent(projectId: number): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/daysWithoutEvent/${projectId}`);
  }
}
